<template>
  <v-card class="billing-plan__card px-0 py-0">
    <v-skeleton-loader
      v-if="invoicesLoading"
      class="skeleton-loader"
      type="heading, table-thead, table-tbody"></v-skeleton-loader>
    <template v-else>
      <div class="d-flex px-4 py-4">
        <v-card-title class="billing-history__card-title mb-0">{{
          $t("settings.billing.billing_history.title")
        }}</v-card-title>
      </div>
      <v-toolbar flat class="v-toolbar">
        <div>
          <v-text-field
            class="search-filter"
            v-model="tableSearch"
            label=""
            :placeholder="$t('generic.search_label')"
            hide-details>
          </v-text-field>
        </div>
        <div class="spacer"></div>
      </v-toolbar>
      <VaClientSideList
        :search="tableSearch"
        class="w-full"
        :headers="headers"
        :items="tableItems"
        :footer-props="{
          showFirstLastPage: false,
          showCurrentPage: true,
          itemsPerPageText: 'Items per page:',
        }">
        <template #item.status="{ item }">
          <v-chip :color="item.chipColor" :text-color="item.chipTextColor">
            {{ item.chipText }}
          </v-chip>
        </template>
        <template #item.actions="{ item }">
          <v-btn
            v-if="item.pdfLink"
            :loading="item.downloading"
            @click="downloadInvoice(item)"
            small
            outlined
            color="subtitle">
            <v-icon class="mr-2" small>mdi-package-down</v-icon
            >{{ $t("settings.billing.billing_history.download") }}</v-btn
          >
        </template>
      </VaClientSideList>
    </template>
  </v-card>
</template>

<script>
  import { INVOICE_STATUS } from "@/constant";
  import VaClientSideList from "@/components/lists/VaClientSideList";
  import { format } from "date-fns";
  import { mapGetters } from "vuex";

  export default {
    name: "BillingHistory",
    components: { VaClientSideList },
    data() {
      return {
        tableSearch: "",
        headers: [
          {
            text: this.$t("settings.billing.billing_history.columns.invoice"),
            value: "name",
            sortable: true,
          },
          {
            text: this.$t("settings.billing.billing_history.columns.amount"),
            value: "formattedAmount",
            sortable: false,
          },
          {
            text: this.$t("settings.billing.billing_history.columns.date"),
            value: "formattedDate",
            sortable: false,
          },
          {
            text: this.$t("settings.billing.billing_history.columns.status"),
            value: "status",
            sortable: false,
          },
          {
            align: "end",
            text: this.$t("settings.billing.billing_history.columns.actions"),
            value: "actions",
            sortable: false,
          },
        ],
        tableItems: [],
      };
    },
    computed: {
      ...mapGetters({
        invoices: "billing/getInvoices",
        invoicesLoading: "billing/getInvoicesLoading",
      }),
    },
    watch: {
      invoices: {
        immediate: true,
        handler(data) {
          if (!data.length) return;

          this.tableItems = data.map(invoice => {
            return {
              ...invoice,
              name: invoice.number ? `${invoice.number}` : "",
              downloading: false,
              ...this.getChipConfig(invoice),
              ...(() => {
                if (invoice?.effectiveAt) {
                  return {
                    formattedDate: format(new Date(invoice.effectiveAt), "MM/dd/yyyy"),
                  };
                }

                return {};
              })(),
            };
          });
        },
      },
    },
    methods: {
      getChipConfig(item) {
        let statusText = "";

        switch (item.status) {
          case INVOICE_STATUS.PAID:
            statusText = this.$t("settings.billing.billing_history.status.paid");
            break;
          case INVOICE_STATUS.DRAFT:
            statusText = this.$t("settings.billing.billing_history.status.draft");
            break;
          case INVOICE_STATUS.OPEN:
            statusText = this.$t("settings.billing.billing_history.status.open");
            break;
          case INVOICE_STATUS.VOID:
            statusText = this.$t("settings.billing.billing_history.status.void");
            break;
          case INVOICE_STATUS.UNCOLLECTIBLE:
            statusText = this.$t("settings.billing.billing_history.status.uncollectible");
            break;
          default:
            statusText = "-";
            break;
        }

        const successStatuses = [INVOICE_STATUS.PAID];
        const neutralStatuses = [INVOICE_STATUS.DRAFT, INVOICE_STATUS.OPEN];
        const errorStatuses = [INVOICE_STATUS.VOID, INVOICE_STATUS.UNCOLLECTIBLE];

        if (successStatuses.includes(item.status)) {
          return {
            chipColor: "success-120",
            chipTextColor: "success",
            chipText: statusText,
          };
        }
        if (neutralStatuses.includes(item.status)) {
          return {
            chipColor: "element-bg-200",
            chipTextColor: "subtitle",
            chipText: statusText,
          };
        }
        if (errorStatuses.includes(item.status)) {
          return {
            chipColor: "error-120",
            chipTextColor: "error",
            chipText: statusText,
          };
        }
      },
      async downloadInvoice(item) {
        const anchor = document.createElement("a");
        anchor.href = item.pdfLink;
        anchor.download = "default-filename.pdf";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@/@core/preset/preset/functions.scss";
  @import "@/@core/preset/preset/typography/index.scss";

  .v-toolbar {
    border-top: 1px solid get-color("border") !important;
    padding: 16px;
    &::v-deep {
      .v-toolbar__content {
        padding: 0 !important;
      }
    }
  }

  .billing-history__card-title {
    margin-bottom: 24px;
  }
  .skeleton-loader {
    &::v-deep {
      .v-skeleton-loader__heading {
        margin: 20px 0 0 20px;
      }
    }
  }
</style>
